import { useState } from 'react';
import type { BaseReactPlayerProps } from 'react-player/base';
import type { VimeoPlayerProps } from 'react-player/vimeo';

import ArtDirectedImage, {
  type ImageSources,
} from '@/components/Image/ArtDirectedImage';
import VideoPlayer from '@/components/VideoPlayer/VideoPlayer';
import type { Contentful } from '@/contentful';
import { brandColors } from '@/styles/theme/values/brandColors';
import { getVideoAspectRatio } from '@/utils/vimeo';

import useStyles from './VimeoVideo.styles';

interface Props extends VimeoPlayerProps {
  thumbnailSources?: ImageSources;
  containerClasses?: string;
  color?: string;
  background?: boolean;
  isRounded?: boolean;
}

export interface VimeoVideoType extends Contentful {
  fields: {
    name: string;
    videoUrl: string;
    autoPlay: boolean;
  };
}

const autoplayProps: BaseReactPlayerProps = {
  playing: true,
  loop: true,
  playsinline: true,
  muted: true,
  controls: false,
};

export default function VimeoVideo({
  thumbnailSources,
  containerClasses,
  color = brandColors.chargeBlue,
  isRounded,
  autoplay,
  ...rest
}: Props) {
  const [hideThumbnail, setHideThumbnail] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<string | null>(null);
  const { classes, cx } = useStyles({ aspectRatio });

  const handleError: VimeoPlayerProps['onError'] = (error) => {
    rest?.onError?.(error);
    if (!error) return;

    if (error.name === 'NotAllowedError' && error.method === 'play') {
      setHideThumbnail(false);
    }
  };

  const handlePlay = () => {
    rest.onPlay?.();
    setHideThumbnail(true);
  };

  const handleReady: VimeoPlayerProps['onReady'] = async (player) => {
    rest.onReady?.(player);
    const videoElement = player.getInternalPlayer();
    const aspectRatio = await getVideoAspectRatio(videoElement);
    setAspectRatio(aspectRatio);

    if (autoplay) {
      // if autoplay playback is on, let onPlay callback hide the thumbnail.
      // this results in a smooth picture to video playback transition.
      return;
    } else {
      // otherwise, hide the thumbnail as soon as the player loads
      // and let the user control the playback
      setHideThumbnail(true);
    }
  };

  return (
    <div
      className={cx(
        classes.container,
        isRounded && classes.rounded,
        containerClasses
      )}
    >
      {thumbnailSources && (
        <ArtDirectedImage
          images={thumbnailSources}
          role='presentation'
          alt=''
          className={cx(classes.absoluteFill, classes.thumbnail, {
            [classes.hidden]: hideThumbnail,
          })}
        />
      )}
      {/** biome-ignore lint/correctness/useUniqueElementIds: Automated migration to Biome */}
      <VideoPlayer
        id='vimeo-player'
        data-testid='video-player'
        width='100%'
        height='100%'
        onError={handleError}
        onPlay={handlePlay}
        onReady={handleReady}
        className={classes.absoluteFill}
        config={{ playerOptions: { color: color.substring(1) } }}
        {...(autoplay ? autoplayProps : {})}
        {...rest}
      />
    </div>
  );
}
